import {
  Button,
  Checkbox,
  Group,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core"
import { Fragment, type MouseEvent, type ReactNode, useEffect, useState } from "react"
import { Link, navigate } from "../../components/link"
import {
  AppCard,
  DataTableShell,
  EmptyState,
  FormActions,
  MetricCard,
  SectionHeader,
  StatusBadge,
} from "../../components/ui"
import type {
  CampaignChannelDetailState,
  CampaignChannelKey,
  CampaignChannelReadiness,
  CampaignChannelRecord,
  CampaignEntityRecord,
  CampaignGenerationState,
  CampaignReadinessAction,
  GoogleSearchNegativeDiagnostic,
  GoogleSearchTermDiagnostic,
  MetaAdResultDiagnostic,
  SiteAssetRecord,
  SiteSegmentRecord,
} from "../../lib/api"
import { formatLocalDateTime } from "../../lib/dates"
import { formatDecimal, formatMicrosCurrency } from "../../lib/format"

type AssetTab = "overview" | "landing" | "targeting" | "copy" | "media"
type CampaignStatsRange = "today" | "yesterday" | "last7Days" | "last30Days"

const campaignStatsRangeLabels: Record<CampaignStatsRange, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last7Days: "7d",
  last30Days: "30d",
}

function summaryNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string") {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function summaryString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null
}

function summaryBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") return value
  if (typeof value === "string") {
    if (value.toLowerCase() === "true") return true
    if (value.toLowerCase() === "false") return false
  }
  return null
}

function formatDateTime(value: string | null): string {
  return formatLocalDateTime(value)
}

function statusLabel(value: string): string {
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

function providerLabel(provider: string): string {
  if (provider === "google_ads" || provider === "google") return "Google Ads"
  if (provider === "meta_ads" || provider === "meta") return "Meta Ads"
  return provider
}

export function CampaignsPanel({
  generation,
  detail,
  detailRoute,
  jobStatusPanel,
  generating,
  actionCampaignId,
  onArchive,
  onGenerate,
  onLaunch,
  onGenerateAssets,
  onOpenAssets,
  onOpenSegment,
  onCloseDetail,
  onDeleteProviderCampaign,
  onOptimize,
  onPullProviderState,
  onAddGoogleSearchNegative,
  onSaveBudget,
  onSavePlannerInstructions,
  onSetServingStatus,
  onUnarchive,
}: {
  generation: CampaignGenerationState | null
  detail: CampaignChannelDetailState | null
  detailRoute: boolean
  jobStatusPanel: ReactNode
  generating: boolean
  actionCampaignId: string | null
  onArchive: (campaignChannelId: string) => void
  onDeleteProviderCampaign: (campaignChannelId: string) => void
  onGenerate: (input: {
    campaignChannelIds?: string[]
    targets?: Array<{
      segmentId: string
      channel: CampaignChannelKey
      dailyBudgetAmount?: string
    }>
  }) => void
  onLaunch: (campaignChannelId: string) => void
  onGenerateAssets: (segmentId: string, action: CampaignReadinessAction, assetTab: AssetTab) => void
  onOpenAssets: () => void
  onOpenSegment: (segmentId: string) => void
  onCloseDetail: () => void
  onAddGoogleSearchNegative: (campaignChannelId: string, term: GoogleSearchTermDiagnostic) => void
  onSaveBudget: (campaignChannelId: string, budgetAmount: string) => void
  onSavePlannerInstructions: (campaignChannelId: string, plannerInstructions: string) => void
  onOptimize: (campaignChannelId: string, dryRun?: boolean) => void
  onPullProviderState: (campaignChannelId: string) => void
  onSetServingStatus: (campaignChannelId: string, status: "active" | "paused") => void
  onUnarchive: (campaignChannelId: string) => void
}) {
  const segmentStates = generation?.segments ?? []
  const eligibleChannels = Array.from(
    new Set(segmentStates.flatMap((segmentState) => segmentState.readiness.eligibleChannels))
  )

  if (detailRoute) {
    return detail ? (
      <CampaignChannelDetailPanel
        actionCampaignId={actionCampaignId}
        detail={detail}
        onArchive={onArchive}
        onBack={onCloseDetail}
        onDeleteProviderCampaign={onDeleteProviderCampaign}
        onOptimize={onOptimize}
        onPullProviderState={onPullProviderState}
        onAddGoogleSearchNegative={onAddGoogleSearchNegative}
        onSaveBudget={onSaveBudget}
        onSavePlannerInstructions={onSavePlannerInstructions}
        onSetServingStatus={onSetServingStatus}
        onSync={onLaunch}
        onUnarchive={onUnarchive}
      />
    ) : (
      <AppCard>
        <SectionHeader title="Campaign channel" description="Loading channel details." />
      </AppCard>
    )
  }

  return (
    <>
      <AppCard>
        <SectionHeader
          title="Campaigns"
          description={`${String(segmentStates.length)} segments · ${String(
            eligibleChannels.length
          )} linked channels`}
        />
        <CampaignSegmentsTable
          actionCampaignId={actionCampaignId}
          generating={generating}
          onArchive={onArchive}
          onGenerate={onGenerate}
          onGenerateAssets={onGenerateAssets}
          onOptimize={onOptimize}
          onOpenSegment={onOpenSegment}
          onPullProviderState={onPullProviderState}
          onSaveBudget={onSaveBudget}
          onSavePlannerInstructions={onSavePlannerInstructions}
          onSetServingStatus={onSetServingStatus}
          onSync={onLaunch}
          onUnarchive={onUnarchive}
          segmentStates={segmentStates}
        />
      </AppCard>

      <AppCard>
        <SectionHeader
          title="Sync controls"
          description="Use row selection above for launch and sync."
        />
        {jobStatusPanel}
        {segmentStates.length > 0 && eligibleChannels.length === 0 ? (
          <Text c="dimmed" size="sm" mt="md">
            No eligible ads channels.
          </Text>
        ) : null}
        {segmentStates.some((segmentState) => !segmentState.readiness.ready) ? (
          <FormActions>
            <Button variant="light" type="button" onClick={onOpenAssets}>
              Fix assets
            </Button>
          </FormActions>
        ) : null}
      </AppCard>
    </>
  )
}

function CampaignSegmentsTable({
  actionCampaignId,
  generating,
  onArchive,
  onGenerate,
  onGenerateAssets,
  onOptimize,
  onOpenSegment,
  onPullProviderState,
  onSaveBudget,
  onSavePlannerInstructions,
  onSetServingStatus,
  onSync,
  onUnarchive,
  segmentStates,
}: {
  actionCampaignId: string | null
  generating: boolean
  onArchive: (campaignChannelId: string) => void
  onGenerate: (input: {
    campaignChannelIds?: string[]
    targets?: Array<{
      segmentId: string
      channel: CampaignChannelKey
      dailyBudgetAmount?: string
    }>
  }) => void
  onGenerateAssets: (segmentId: string, action: CampaignReadinessAction, assetTab: AssetTab) => void
  onOptimize: (campaignChannelId: string, dryRun?: boolean) => void | Promise<void>
  onOpenSegment: (segmentId: string) => void
  onPullProviderState: (campaignChannelId: string) => void | Promise<void>
  onSaveBudget: (campaignChannelId: string, budgetAmount: string) => void
  onSavePlannerInstructions: (campaignChannelId: string, plannerInstructions: string) => void
  onSetServingStatus: (
    campaignChannelId: string,
    status: "active" | "paused"
  ) => void | Promise<void>
  onSync: (campaignChannelId: string) => void
  onUnarchive: (campaignChannelId: string) => void
  segmentStates: CampaignGenerationState["segments"]
}) {
  const [showArchived, setShowArchived] = useState(false)
  const [statsRange, setStatsRange] = useState<CampaignStatsRange>("last7Days")
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [expandedReadinessRows, setExpandedReadinessRows] = useState<string[]>([])
  const channels = segmentStates.flatMap((segmentState) => segmentState.channels)
  const archivedCount = channels.filter((channel) => channel.status === "archived").length
  const hasEligibleChannels = segmentStates.some(
    (segmentState) => segmentState.readiness.eligibleChannels.length > 0
  )
  const rows = campaignControlRows(segmentStates, showArchived)
  const selected = rows.filter((row) => selectedRows.includes(row.key))
  const selectedLaunched = selected.filter((row) => Boolean(row.channel?.externalCampaignId))
  const selectedUnlaunched = selected.filter((row) => !row.channel?.externalCampaignId)
  const selectableRows = rows.filter((row) => {
    const readiness = segmentStates
      .find((segmentState) => segmentState.segment.segmentId === row.segment.segmentId)
      ?.readiness.channelReadiness?.find((candidate) => candidate.channel === row.channelKey)
    return row.channel ? row.channel.status !== "archived" : (readiness?.ready ?? true)
  })
  const selectedSelectableCount = selectableRows.filter((row) =>
    selectedRows.includes(row.key)
  ).length
  const allVisibleSelected =
    selectableRows.length > 0 && selectableRows.every((row) => selectedRows.includes(row.key))
  const selectedChannels = selected
    .map((row) => row.channel)
    .filter((channel): channel is CampaignChannelRecord => Boolean(channel))
  const [budgetDrafts, setBudgetDrafts] = useState<Record<string, string>>({})
  const selectedLaunchedChannels = selectedChannels.filter((channel) =>
    Boolean(channel.externalCampaignId)
  )
  const selectedActiveChannels = selectedLaunchedChannels.filter(
    (channel) => channel.status === "active"
  )
  const selectedPausedChannels = selectedLaunchedChannels.filter(
    (channel) => channel.status !== "active"
  )
  const bulkDisabled = generating || selected.length === 0
  const selectedUnlaunchedMissingBudget = selectedUnlaunched.some(
    (row) => !hasPositiveBudget(campaignRowBudgetValue(row, budgetDrafts))
  )

  function toggleRow(rowKey: string, checked: boolean) {
    setSelectedRows((current) =>
      checked ? Array.from(new Set([...current, rowKey])) : current.filter((key) => key !== rowKey)
    )
  }

  function toggleReadiness(rowKey: string) {
    setExpandedReadinessRows((current) =>
      current.includes(rowKey) ? current.filter((key) => key !== rowKey) : [...current, rowKey]
    )
  }

  function toggleAllVisible(checked: boolean) {
    setSelectedRows((current) => {
      const selectableKeys = selectableRows.map((row) => row.key)
      if (!checked) return current.filter((key) => !selectableKeys.includes(key))
      return Array.from(new Set([...current, ...selectableKeys]))
    })
  }

  function runBulkAction() {
    if (selected.length === 0) return
    if (selectedLaunched.length === selected.length) {
      onGenerate({
        campaignChannelIds: selectedLaunched
          .map((row) => row.channel?.campaignChannelId)
          .filter((campaignChannelId): campaignChannelId is string => Boolean(campaignChannelId)),
      })
      return
    }
    if (selectedUnlaunched.length === selected.length) {
      if (selectedUnlaunchedMissingBudget) return
      onGenerate({
        targets: selectedUnlaunched.map((row) => ({
          segmentId: row.segment.segmentId,
          channel: row.channelKey,
          dailyBudgetAmount: campaignRowBudgetValue(row, budgetDrafts),
        })),
      })
    }
  }

  function runBulkLaunched(action: (campaignChannelId: string) => void | Promise<void>) {
    void Promise.all(selectedLaunchedChannels.map((channel) => action(channel.campaignChannelId)))
  }

  function runBulkServingStatus(status: "active" | "paused") {
    const channels = status === "active" ? selectedPausedChannels : selectedActiveChannels
    void Promise.all(
      channels.map((channel) => onSetServingStatus(channel.campaignChannelId, status))
    )
  }

  if (segmentStates.length === 0) {
    return (
      <>
        <CampaignTableControls
          archivedCount={archivedCount}
          statsRange={statsRange}
          showArchived={showArchived}
          onStatsRangeChange={setStatsRange}
          onShowArchivedChange={setShowArchived}
        />
        <EmptyState>Campaign readiness is loading.</EmptyState>
      </>
    )
  }

  if (!hasEligibleChannels) {
    return (
      <>
        <CampaignTableControls
          archivedCount={archivedCount}
          statsRange={statsRange}
          showArchived={showArchived}
          onStatsRangeChange={setStatsRange}
          onShowArchivedChange={setShowArchived}
        />
        <EmptyState>No linked ads channels.</EmptyState>
      </>
    )
  }

  return (
    <>
      <CampaignTableControls
        archivedCount={archivedCount}
        statsRange={statsRange}
        showArchived={showArchived}
        onStatsRangeChange={setStatsRange}
        onShowArchivedChange={setShowArchived}
      />
      {selected.length > 0 ? (
        <Group className="campaign-bulk-bar" gap="sm" mt="md">
          <Text c="dimmed" size="sm">
            {String(selected.length)} selected
          </Text>
          <Button
            aria-label={
              selectedLaunched.length === selected.length ? "Sync selected" : "Launch selected"
            }
            disabled={
              bulkDisabled ||
              (selectedLaunched.length > 0 && selectedUnlaunched.length > 0) ||
              selectedUnlaunchedMissingBudget
            }
            type="button"
            onClick={runBulkAction}
          >
            {generating
              ? "Working..."
              : selectedLaunched.length === selected.length
                ? "Sync"
                : "Launch"}
          </Button>
          <Button
            disabled={bulkDisabled || selectedLaunchedChannels.length === 0}
            type="button"
            variant="light"
            onClick={() => runBulkLaunched(onPullProviderState)}
          >
            Pull stats
          </Button>
          <Button
            disabled={bulkDisabled || selectedLaunchedChannels.length === 0}
            type="button"
            variant="light"
            onClick={() =>
              runBulkLaunched((campaignChannelId) => onOptimize(campaignChannelId, true))
            }
          >
            Review recs
          </Button>
          <Button
            disabled={bulkDisabled || selectedLaunchedChannels.length === 0}
            type="button"
            variant="light"
            onClick={() => runBulkLaunched((campaignChannelId) => onOptimize(campaignChannelId))}
          >
            Optimize
          </Button>
          <Button
            disabled={bulkDisabled || selectedPausedChannels.length === 0}
            type="button"
            variant="light"
            onClick={() => runBulkServingStatus("active")}
          >
            Resume
          </Button>
          <Button
            disabled={bulkDisabled || selectedActiveChannels.length === 0}
            type="button"
            variant="light"
            onClick={() => runBulkServingStatus("paused")}
          >
            Pause
          </Button>
          {selected.length > 0 && selectedLaunched.length > 0 && selectedUnlaunched.length > 0 ? (
            <Text c="dimmed" size="sm">
              Mixed selection: launch or sync one group at a time.
            </Text>
          ) : null}
          {selectedUnlaunchedMissingBudget ? (
            <Text c="dimmed" size="sm">
              Set a positive budget before launch.
            </Text>
          ) : null}
        </Group>
      ) : null}
      <DataTableShell className="asset-table campaign-table" minWidth={1100} mt="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Checkbox
                aria-label="Select visible campaign channels"
                checked={allVisibleSelected}
                indeterminate={selectedSelectableCount > 0 && !allVisibleSelected}
                onChange={(event) => toggleAllVisible(event.target.checked)}
              />
            </Table.Th>
            <Table.Th>Seg</Table.Th>
            <Table.Th>Ch</Table.Th>
            <Table.Th>Ready</Table.Th>
            <Table.Th aria-label="Status" />
            <Table.Th>Op</Table.Th>
            <Table.Th>Budget</Table.Th>
            <Table.Th>{`Spend ${campaignStatsRangeLabels[statsRange]}`}</Table.Th>
            <Table.Th>{`Conv ${campaignStatsRangeLabels[statsRange]}`}</Table.Th>
            <Table.Th>{`CPA ${campaignStatsRangeLabels[statsRange]}`}</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th className="data-table__actions">Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        {segmentStates.map((segmentState) => (
          <Table.Tbody key={segmentState.segment.segmentId}>
            <Table.Tr className="campaign-segment-row">
              <Table.Td colSpan={12}>
                <div className="campaign-segment-summary">
                  <span>
                    <button
                      className="table-name-link"
                      type="button"
                      onClick={() => onOpenSegment(segmentState.segment.segmentId)}
                    >
                      {segmentState.segment.name}
                    </button>
                    <small>{segmentReadinessMessage(segmentState.readiness)}</small>
                  </span>
                  <CampaignSegmentStats
                    rangeLabel={campaignStatsRangeLabels[statsRange]}
                    rollup={campaignRowsRollup(
                      rows.filter(
                        (row) => row.segment.segmentId === segmentState.segment.segmentId
                      ),
                      statsRange
                    )}
                  />
                  <span className="campaign-segment-score">
                    <strong>{String(segmentState.readiness.score)}</strong>
                    <span>ready</span>
                  </span>
                </div>
              </Table.Td>
            </Table.Tr>
            {rows
              .filter((row) => row.segment.segmentId === segmentState.segment.segmentId)
              .map((row) => {
                const channel = row.channel
                const budgetValue = campaignRowBudgetValue(row, budgetDrafts)
                const channelReadiness = channelReadinessForRow(segmentState, row.channelKey)
                const readinessExpanded = expandedReadinessRows.includes(row.key)
                const channelStats = channel ? campaignChannelStats(channel, statsRange) : null
                if (!channel) {
                  return (
                    <Fragment key={row.key}>
                      <Table.Tr
                        aria-label={`${segmentState.segment.name} ${campaignChannelLabel(
                          row.channelKey
                        )} campaign channel`}
                        className="campaign-channel-row campaign-channel-row--virtual"
                      >
                        <Table.Td onClick={(event) => event.stopPropagation()}>
                          <Checkbox
                            aria-label={`Select ${segmentState.segment.name} ${campaignChannelLabel(
                              row.channelKey
                            )}`}
                            checked={selectedRows.includes(row.key)}
                            disabled={channelReadiness ? !channelReadiness.ready : false}
                            onChange={(event) => toggleRow(row.key, event.target.checked)}
                          />
                        </Table.Td>
                        <Table.Td />
                        <Table.Td>{campaignChannelLabel(row.channelKey)}</Table.Td>
                        <Table.Td>
                          <ChannelReadinessButton
                            expanded={readinessExpanded}
                            readiness={channelReadiness}
                            onClick={() => toggleReadiness(row.key)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <CampaignStatusDot status="draft" />
                        </Table.Td>
                        <Table.Td>Not queued</Table.Td>
                        <Table.Td onClick={(event) => event.stopPropagation()}>
                          <CampaignBudgetCell
                            budgetAmount={budgetValue}
                            onChange={(value) =>
                              setBudgetDrafts((current) => ({ ...current, [row.key]: value }))
                            }
                          />
                        </Table.Td>
                        <Table.Td>
                          <CampaignStatsValue value={null} />
                        </Table.Td>
                        <Table.Td>
                          <CampaignStatsValue value={null} />
                        </Table.Td>
                        <Table.Td>
                          <CampaignStatsValue value={null} />
                        </Table.Td>
                        <Table.Td>
                          <CampaignProviderState channel={null} />
                        </Table.Td>
                        <Table.Td className="data-table__actions">
                          <Button
                            disabled={
                              generating ||
                              (channelReadiness ? !channelReadiness.ready : false) ||
                              !hasPositiveBudget(budgetValue)
                            }
                            size="xs"
                            type="button"
                            variant="subtle"
                            onClick={() =>
                              onGenerate({
                                targets: [
                                  {
                                    segmentId: row.segment.segmentId,
                                    channel: row.channelKey,
                                    dailyBudgetAmount: budgetValue,
                                  },
                                ],
                              })
                            }
                          >
                            {!hasPositiveBudget(budgetValue)
                              ? "Set budget"
                              : channelReadiness && !channelReadiness.ready
                                ? "Fix readiness"
                                : "Launch"}
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                      {readinessExpanded ? (
                        <Table.Tr className="campaign-channel-readiness-row">
                          <Table.Td colSpan={12}>
                            <ChannelReadinessDetails
                              readiness={channelReadiness}
                              segmentId={row.segment.segmentId}
                              onGenerateAssets={onGenerateAssets}
                            />
                          </Table.Td>
                        </Table.Tr>
                      ) : null}
                    </Fragment>
                  )
                }
                const rowBusy = actionCampaignId === channel.campaignChannelId
                return (
                  <Fragment key={row.key}>
                    <Table.Tr
                      aria-label={`${segmentState.segment.name} ${campaignChannelLabel(
                        channel.channel
                      )} campaign channel`}
                      className="campaign-channel-row campaign-channel-row--clickable"
                      onClick={() => navigate(campaignChannelDetailUrl(channel))}
                    >
                      <Table.Td onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          aria-label={`Select ${segmentState.segment.name} ${campaignChannelLabel(
                            row.channelKey
                          )}`}
                          checked={selectedRows.includes(row.key)}
                          disabled={channelReadiness ? !channelReadiness.ready : false}
                          onChange={(event) => toggleRow(row.key, event.target.checked)}
                        />
                      </Table.Td>
                      <Table.Td />
                      <Table.Td>{campaignChannelLabel(channel.channel)}</Table.Td>
                      <Table.Td onClick={(event) => event.stopPropagation()}>
                        <ChannelReadinessButton
                          expanded={readinessExpanded}
                          readiness={channelReadiness}
                          onClick={() => toggleReadiness(row.key)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <CampaignStatusDot status={channel.status} />
                      </Table.Td>
                      <Table.Td>
                        <CampaignOperationStatus channel={channel} />
                      </Table.Td>
                      <Table.Td onClick={(event) => event.stopPropagation()}>
                        <CampaignBudgetCell
                          budgetAmount={budgetValue}
                          onChange={(value) =>
                            setBudgetDrafts((current) => ({ ...current, [row.key]: value }))
                          }
                          onSave={() => onSaveBudget(channel.campaignChannelId, budgetValue)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <CampaignStatsValue value={channelStats?.spendLabel ?? null} />
                      </Table.Td>
                      <Table.Td>
                        <CampaignStatsValue value={channelStats?.conversionsLabel ?? null} />
                      </Table.Td>
                      <Table.Td>
                        <CampaignStatsValue value={channelStats?.cpaLabel ?? null} />
                      </Table.Td>
                      <Table.Td>
                        <CampaignProviderState channel={channel} />
                      </Table.Td>
                      <Table.Td className="data-table__actions">
                        <CampaignActionsMenu
                          busy={rowBusy}
                          channel={channel}
                          onArchive={onArchive}
                          canSync={
                            Boolean(channel.externalCampaignId) || hasPositiveBudget(budgetValue)
                          }
                          onSaveBudget={() => onSaveBudget(channel.campaignChannelId, budgetValue)}
                          onSavePlannerInstructions={onSavePlannerInstructions}
                          onSync={onSync}
                          onUnarchive={onUnarchive}
                        />
                      </Table.Td>
                    </Table.Tr>
                    {readinessExpanded ? (
                      <Table.Tr className="campaign-channel-readiness-row">
                        <Table.Td colSpan={12}>
                          <ChannelReadinessDetails
                            readiness={channelReadiness}
                            segmentId={row.segment.segmentId}
                            onGenerateAssets={onGenerateAssets}
                          />
                        </Table.Td>
                      </Table.Tr>
                    ) : null}
                  </Fragment>
                )
              })}
          </Table.Tbody>
        ))}
      </DataTableShell>
    </>
  )
}

function CampaignBudgetCell({
  budgetAmount,
  onChange,
  onSave,
}: {
  budgetAmount: string
  onChange: (value: string) => void
  onSave?: () => void
}) {
  const [editing, setEditing] = useState(false)
  const displayValue = budgetAmount.trim() ? `$${budgetAmount.trim()}` : "Set budget"

  function save() {
    setEditing(false)
    onSave?.()
  }

  if (!editing) {
    return (
      <button
        aria-label="Edit budget cap"
        className="campaign-budget-value"
        type="button"
        onClick={() => setEditing(true)}
      >
        {displayValue}
      </button>
    )
  }

  return (
    <Group className="campaign-budget-cell" gap={4} wrap="nowrap">
      <TextInput
        aria-label="Budget cap"
        autoFocus
        className="campaign-budget-input"
        inputMode="decimal"
        placeholder="0.00"
        value={budgetAmount}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") save()
          if (event.key === "Escape") setEditing(false)
        }}
      />
      {onSave ? (
        <Button size="xs" type="button" variant="subtle" onClick={save}>
          Save
        </Button>
      ) : null}
    </Group>
  )
}

function campaignRowBudgetValue(
  row: CampaignControlRow,
  budgetDrafts: Record<string, string>
): string {
  return budgetDrafts[row.key] ?? row.channel?.budgetAmount ?? ""
}

function hasPositiveBudget(value: string | null | undefined): boolean {
  if (!value?.trim()) return false
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0
}

interface CampaignRowsRollup {
  total: number
  launched: number
  draft: number
  active: number
  paused: number
  dirty: number
  failed: number
  archived: number
  pulled: number
  clicks: number
  costMicros: number
  conversions: number
}

function campaignRowsRollup(
  rows: CampaignControlRow[],
  statsRange: CampaignStatsRange
): CampaignRowsRollup {
  return rows.reduce<CampaignRowsRollup>(
    (rollup, row) => {
      rollup.total += 1
      const channel = row.channel
      if (!channel?.externalCampaignId) {
        rollup.draft += 1
        return rollup
      }
      rollup.launched += 1
      if (channel.status === "active") rollup.active += 1
      if (channel.status === "paused") rollup.paused += 1
      if (channel.status === "archived") rollup.archived += 1
      if (channel.syncState === "needs_sync") rollup.dirty += 1
      if (channel.syncState === "failed") rollup.failed += 1
      const metrics = campaignChannelSnapshotMetrics(channel, statsRange)
      if (metrics) {
        rollup.pulled += 1
        rollup.clicks += metrics.clicks
        rollup.costMicros += metrics.costMicros
        rollup.conversions += metrics.conversions
      }
      return rollup
    },
    {
      total: 0,
      launched: 0,
      draft: 0,
      active: 0,
      paused: 0,
      dirty: 0,
      failed: 0,
      archived: 0,
      pulled: 0,
      clicks: 0,
      costMicros: 0,
      conversions: 0,
    }
  )
}

function CampaignSegmentStats({
  rangeLabel,
  rollup,
}: {
  rangeLabel: string
  rollup: CampaignRowsRollup
}) {
  const cpa =
    rollup.pulled && rollup.conversions > 0
      ? formatMicrosCurrency(rollup.costMicros / rollup.conversions)
      : "-"
  return (
    <div className="campaign-segment-stats">
      <span>{`${String(rollup.launched)}/${String(rollup.total)} launched`}</span>
      <span>
        {rollup.pulled ? `${formatMicrosCurrency(rollup.costMicros)} ${rangeLabel}` : "- spend"}
      </span>
      <span>
        {rollup.pulled ? `${formatDecimal(rollup.conversions)} conv ${rangeLabel}` : "- conv"}
      </span>
      <span>{`${cpa} CPA ${rangeLabel}`}</span>
      {rollup.dirty ? (
        <span className="campaign-segment-stats--warn">{String(rollup.dirty)} dirty</span>
      ) : null}
      {rollup.failed ? (
        <span className="campaign-segment-stats--bad">{String(rollup.failed)} failed</span>
      ) : null}
    </div>
  )
}

function CampaignStatusDot({ status }: { status: string }) {
  const label = statusLabel(status)
  return (
    <span
      aria-label={label}
      className={`campaign-status-dot campaign-status-dot--${campaignStatusTone(status)}`}
      role="img"
      title={label}
    />
  )
}

function CampaignProviderState({ channel }: { channel: CampaignChannelRecord | null }) {
  if (!channel?.externalCampaignId) {
    return (
      <div className="campaign-provider-state">
        <span>Not launched</span>
      </div>
    )
  }
  return (
    <div className="campaign-provider-state">
      <span>{providerLabel(channel.provider)}</span>
      <small>
        {channel.latestSnapshot
          ? `Pulled ${formatDateTime(channel.latestSnapshot.capturedAt)}`
          : channel.lastSyncedAt
            ? `Synced ${formatDateTime(channel.lastSyncedAt)}`
            : "No sync yet"}
      </small>
    </div>
  )
}

function campaignObservedProviderStatus(channel: CampaignChannelRecord | null): string | null {
  if (!channel?.externalCampaignId) return null
  const snapshot = channel.latestSnapshot
  const status = summaryString(snapshot?.status)
  if (status && status.toLowerCase() !== "unknown") return status
  const summaryStatus = summaryString(snapshot?.summary?.status)
  return summaryStatus && summaryStatus.toLowerCase() !== "unknown" ? summaryStatus : null
}

function isProviderPausedStatus(status: string | null): boolean {
  if (!status) return false
  return ["paused", "removed", "deleted", "inactive"].includes(status.toLowerCase())
}

function isProviderActiveStatus(status: string | null): boolean {
  if (!status) return false
  return ["active", "enabled"].includes(status.toLowerCase())
}

function providerObservedStatusTone(status: string): string {
  if (isProviderActiveStatus(status)) return "green"
  if (isProviderPausedStatus(status)) return "yellow"
  return "gray"
}

function providerObservedStatusMatchesIntent(
  channelStatus: string,
  observedStatus: string
): boolean {
  if (channelStatus === "active") return isProviderActiveStatus(observedStatus)
  if (channelStatus === "paused") return isProviderPausedStatus(observedStatus)
  return false
}

function CampaignObservedProviderBadge({
  channel,
  status,
}: {
  channel: CampaignChannelRecord
  status: string | null
}) {
  if (!status) return null
  if (providerObservedStatusMatchesIntent(channel.status, status)) return null
  return (
    <span
      className={`campaign-observed-provider-badge campaign-observed-provider-badge--${providerObservedStatusTone(
        status
      )}`}
    >
      {providerLabel(channel.provider)} last seen: {statusLabel(status)}
    </span>
  )
}

function CampaignStatsValue({ value }: { value: string | null }) {
  return (
    <span
      className={value ? "campaign-stat-value" : "campaign-stat-value campaign-stat-value--empty"}
    >
      {value ?? "-"}
    </span>
  )
}

function campaignChannelStats(
  channel: CampaignChannelRecord,
  statsRange: CampaignStatsRange
): {
  spendLabel: string
  conversionsLabel: string
  cpaLabel: string
} | null {
  const metrics = campaignChannelSnapshotMetrics(channel, statsRange)
  if (!metrics) return null
  return {
    spendLabel: formatMicrosCurrency(metrics.costMicros),
    conversionsLabel: formatDecimal(metrics.conversions),
    cpaLabel:
      metrics.conversions > 0
        ? formatMicrosCurrency(metrics.costMicros / metrics.conversions)
        : "-",
  }
}

function campaignChannelSnapshotMetrics(
  channel: CampaignChannelRecord,
  range: CampaignStatsRange
): { clicks: number; costMicros: number; conversions: number } | null {
  const summary = channel.latestSnapshot?.summary ?? null
  if (!summary) return null
  const metrics = summaryRecord(summary, `metrics.${range}`)
  return {
    clicks: summaryNumber(metrics.clicks) ?? 0,
    costMicros: summaryNumber(metrics.costMicros) ?? 0,
    conversions: summaryNumber(metrics.conversions) ?? 0,
  }
}

function CampaignOperationStatus({ channel }: { channel: CampaignChannelRecord }) {
  return (
    <div className="campaign-operation-status">
      <StatusBadge color={campaignSyncStateColor(channel.syncState)}>
        {statusLabel(channel.syncState)}
      </StatusBadge>
      {channel.syncState === "needs_sync" ? <small>Source changed</small> : null}
      {channel.lastSyncError ? <small>{channel.lastSyncError}</small> : null}
    </div>
  )
}

function ChannelReadinessButton({
  expanded,
  readiness,
  onClick,
}: {
  expanded: boolean
  readiness: CampaignChannelReadiness | null
  onClick: () => void
}) {
  if (!readiness) return <StatusBadge color="gray">Not available</StatusBadge>
  return (
    <Button
      aria-label={`${String(readiness.score)}% ready`}
      color={readiness.ready ? "green" : readiness.score >= 70 ? "yellow" : "red"}
      size="xs"
      type="button"
      variant="light"
      onClick={onClick}
    >
      {expanded ? "Hide" : `${String(readiness.score)}%`}
    </Button>
  )
}

function ChannelReadinessDetails({
  readiness,
  segmentId,
  onGenerateAssets,
}: {
  readiness: CampaignChannelReadiness | null
  segmentId: string
  onGenerateAssets: (segmentId: string, action: CampaignReadinessAction, assetTab: AssetTab) => void
}) {
  if (!readiness) return <Text c="dimmed">Readiness is not available for this channel.</Text>
  return (
    <div className="campaign-channel-readiness-panel">
      <div className="campaign-channel-readiness-panel__header">
        <strong>{readiness.label} readiness</strong>
        <StatusBadge color={readiness.ready ? "green" : "gray"}>
          {readiness.ready ? "Ready" : "Needs assets"}
        </StatusBadge>
      </div>
      <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="sm">
        {readiness.items.map((item) => (
          <div
            className={`campaign-channel-readiness-item${
              item.ready ? " campaign-channel-readiness-item--ready" : ""
            }`}
            key={`${readiness.channel}:${item.key}`}
          >
            <span>{item.label}</span>
            <strong>
              {String(item.points)}/{String(item.maxPoints)}
            </strong>
            <small>{item.message}</small>
            <ReadinessActionButton
              action={item.action}
              segmentId={segmentId}
              onGenerateAssets={onGenerateAssets}
            />
          </div>
        ))}
      </SimpleGrid>
    </div>
  )
}

function ReadinessActionButton({
  action,
  segmentId,
  onGenerateAssets,
}: {
  action: CampaignReadinessAction | null
  segmentId: string
  onGenerateAssets: (segmentId: string, action: CampaignReadinessAction, assetTab: AssetTab) => void
}) {
  if (!action) return null
  return (
    <Button
      size="xs"
      type="button"
      variant="subtle"
      onClick={() => onGenerateAssets(segmentId, action, assetTabForAction(action))}
    >
      {action.label}
    </Button>
  )
}

function assetTabForAction(action: CampaignReadinessAction): AssetTab {
  if (action.scope === "landing") return "landing"
  if (action.scope === "targeting") return "targeting"
  if (action.scope === "copy") return "copy"
  return "media"
}

function CampaignChannelDetailPanel({
  actionCampaignId,
  detail,
  onArchive,
  onBack,
  onDeleteProviderCampaign,
  onOptimize,
  onPullProviderState,
  onAddGoogleSearchNegative,
  onSaveBudget,
  onSavePlannerInstructions,
  onSetServingStatus,
  onSync,
  onUnarchive,
}: {
  actionCampaignId: string | null
  detail: CampaignChannelDetailState
  onArchive: (campaignChannelId: string) => void
  onBack: () => void
  onDeleteProviderCampaign: (campaignChannelId: string) => void
  onOptimize: (campaignChannelId: string, dryRun?: boolean) => void
  onPullProviderState: (campaignChannelId: string) => void
  onAddGoogleSearchNegative: (campaignChannelId: string, term: GoogleSearchTermDiagnostic) => void
  onSaveBudget: (campaignChannelId: string, budgetAmount: string) => void
  onSavePlannerInstructions: (campaignChannelId: string, plannerInstructions: string) => void
  onSetServingStatus: (campaignChannelId: string, status: "active" | "paused") => void
  onSync: (campaignChannelId: string) => void
  onUnarchive: (campaignChannelId: string) => void
}) {
  const channel = detail.channel
  const [budgetDraft, setBudgetDraft] = useState(channel.budgetAmount ?? "")
  const [editingPlannerInstructions, setEditingPlannerInstructions] = useState(false)
  const busy = actionCampaignId === channel.campaignChannelId
  const archived = channel.status === "archived"
  const syncing = channel.syncState === "syncing"
  const observedStatus = campaignObservedProviderStatus(channel)
  const showResume =
    Boolean(channel.externalCampaignId) &&
    (isProviderPausedStatus(observedStatus) || (!observedStatus && channel.status !== "active"))
  const showPause =
    Boolean(channel.externalCampaignId) &&
    (isProviderActiveStatus(observedStatus) || (!observedStatus && channel.status === "active"))
  const canRunProviderAction = !busy && !archived && !syncing
  const canSync =
    canRunProviderAction && (Boolean(channel.externalCampaignId) || hasPositiveBudget(budgetDraft))
  const canDeleteProviderCampaign =
    Boolean(channel.externalCampaignId) || channel.syncState === "failed"
  const metaAdPreviews = buildMetaAdPreviews(detail)
  const googleSearchAdPreviews = buildGoogleSearchAdPreviews(detail)
  const adPreviews = [...metaAdPreviews, ...googleSearchAdPreviews]
  const adPreviewsByEntityId = new Map(adPreviews.map((preview) => [preview.id, preview]))
  const [previewAd, setPreviewAd] = useState<AdPreview | null>(null)

  useEffect(() => {
    setBudgetDraft(channel.budgetAmount ?? "")
  }, [channel.budgetAmount])

  return (
    <AppCard>
      <div className="campaign-channel-detail-header">
        <button className="segment-back-link" type="button" onClick={onBack}>
          Back to campaigns
        </button>
        <div>
          <h2>{channel.name ?? campaignChannelLabel(channel.channel)}</h2>
          <p>
            {campaignChannelLabel(channel.channel)} · {channel.status} · {channel.syncState}
            <CampaignObservedProviderBadge channel={channel} status={observedStatus} />
          </p>
        </div>
      </div>
      <Group className="campaign-detail-actions" align="flex-end" gap="xs" mt="md">
        <TextInput
          label="Budget cap"
          className="campaign-budget-input"
          inputMode="decimal"
          placeholder="Blank"
          value={budgetDraft}
          onChange={(event) => setBudgetDraft(event.target.value)}
        />
        <Button
          disabled={busy}
          size="xs"
          type="button"
          variant="subtle"
          onClick={() => onSaveBudget(channel.campaignChannelId, budgetDraft)}
        >
          Save budget
        </Button>
        <Button
          disabled={!canSync}
          size="xs"
          type="button"
          onClick={() => onSync(channel.campaignChannelId)}
        >
          {busy || syncing ? "Working..." : channel.externalCampaignId ? "Sync" : "Launch"}
        </Button>
        {channel.externalCampaignId ? (
          <Button
            disabled={!canRunProviderAction}
            size="xs"
            type="button"
            variant="subtle"
            onClick={() => onPullProviderState(channel.campaignChannelId)}
          >
            Pull stats
          </Button>
        ) : null}
        {adPreviews.length > 0 ? (
          <Button
            size="xs"
            type="button"
            variant="subtle"
            onClick={() => setPreviewAd(adPreviews[0] ?? null)}
          >
            Preview ad
          </Button>
        ) : null}
        <Button
          disabled={busy || syncing}
          size="xs"
          type="button"
          variant="subtle"
          onClick={() => setEditingPlannerInstructions(true)}
        >
          Planner prompt
        </Button>
        {channel.externalCampaignId ? (
          <Button
            disabled={!canRunProviderAction}
            size="xs"
            type="button"
            variant="light"
            onClick={() => onOptimize(channel.campaignChannelId, true)}
          >
            Review recs
          </Button>
        ) : null}
        {channel.externalCampaignId ? (
          <Button
            disabled={!canRunProviderAction}
            size="xs"
            type="button"
            onClick={() => onOptimize(channel.campaignChannelId)}
          >
            Optimize
          </Button>
        ) : null}
        {showResume ? (
          <Button
            disabled={!canRunProviderAction}
            size="xs"
            type="button"
            color="green"
            onClick={() => onSetServingStatus(channel.campaignChannelId, "active")}
          >
            Resume
          </Button>
        ) : null}
        {showPause ? (
          <Button
            disabled={!canRunProviderAction}
            size="xs"
            type="button"
            variant="subtle"
            onClick={() => onSetServingStatus(channel.campaignChannelId, "paused")}
          >
            Pause
          </Button>
        ) : null}
        {canDeleteProviderCampaign ? (
          <Button
            color="red"
            disabled={busy || syncing}
            size="xs"
            type="button"
            variant="subtle"
            onClick={() => onDeleteProviderCampaign(channel.campaignChannelId)}
          >
            {channel.externalCampaignId ? "Delete provider campaign" : "Clear failed launch"}
          </Button>
        ) : null}
        {archived ? (
          <Button
            disabled={busy}
            size="xs"
            type="button"
            variant="subtle"
            onClick={() => onUnarchive(channel.campaignChannelId)}
          >
            Unarchive
          </Button>
        ) : (
          <Button
            disabled={busy}
            size="xs"
            type="button"
            variant="subtle"
            onClick={() => onArchive(channel.campaignChannelId)}
          >
            Archive
          </Button>
        )}
      </Group>
      {editingPlannerInstructions ? (
        <PlannerInstructionsModal
          busy={busy}
          channel={channel}
          onClose={() => setEditingPlannerInstructions(false)}
          onSave={(plannerInstructions) => {
            setEditingPlannerInstructions(false)
            onSavePlannerInstructions(channel.campaignChannelId, plannerInstructions)
          }}
        />
      ) : null}
      <div className="site-readiness-grid site-readiness-grid--inline">
        <div className="site-readiness-item">
          <span>Provider</span>
          <strong>{providerLabel(channel.provider)}</strong>
        </div>
        <div className="site-readiness-item">
          <span>Provider state</span>
          <strong>{channel.externalCampaignId ? "Launched" : "Not launched"}</strong>
        </div>
        <div className="site-readiness-item">
          <span>Last synced</span>
          <strong>{channel.lastSyncedAt ? formatDateTime(channel.lastSyncedAt) : "Never"}</strong>
        </div>
      </div>
      <CampaignChannelSnapshotSummary detail={detail} />
      <CampaignOptimizationSummary detail={detail} />
      <GoogleSearchTermsPanel
        busy={busy}
        detail={detail}
        onAddNegative={(term) => onAddGoogleSearchNegative(channel.campaignChannelId, term)}
      />
      <MetaAdResultsPanel detail={detail} />
      <DataTableShell className="campaign-entity-table" minWidth={920} mt="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Entity</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Details</Table.Th>
            <Table.Th>Source</Table.Th>
            <Table.Th>Updated</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {detail.entities.length > 0 ? (
            campaignEntityRows(detail.entities).map(({ entity, depth }) => (
              <Table.Tr
                key={entity.campaignEntityId}
                className={depth > 0 ? "campaign-entity-row--child" : undefined}
              >
                <Table.Td>
                  <CampaignEntityTypeBadge entity={entity} />
                </Table.Td>
                <Table.Td style={{ paddingLeft: depth ? `${18 + depth * 28}px` : undefined }}>
                  <CampaignEntityName entity={entity} depth={depth} />
                </Table.Td>
                <Table.Td>
                  <CampaignEntityStatusBadge entity={entity} />
                </Table.Td>
                <Table.Td>
                  <CampaignEntityDetails
                    entity={entity}
                    preview={adPreviewsByEntityId.get(entity.campaignEntityId)}
                    onPreview={setPreviewAd}
                  />
                </Table.Td>
                <Table.Td>
                  <CampaignEntitySourceLink channel={channel} entity={entity} />
                </Table.Td>
                <Table.Td>{formatDateTime(entity.updatedAt)}</Table.Td>
              </Table.Tr>
            ))
          ) : (
            <Table.Tr>
              <Table.Td colSpan={6}>No synced provider entities yet.</Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </DataTableShell>
      {previewAd?.kind === "meta" ? (
        <MetaAdPreviewModal ad={previewAd} onClose={() => setPreviewAd(null)} />
      ) : null}
      {previewAd?.kind === "google_search" ? (
        <GoogleSearchAdPreviewModal ad={previewAd} onClose={() => setPreviewAd(null)} />
      ) : null}
    </AppCard>
  )
}

interface MetaAdPreview {
  kind: "meta"
  id: string
  pageName: string
  primaryText: string
  headline: string
  description: string | null
  imageUrl: string | null
  finalUrl: string | null
  cta: string
}

interface GoogleSearchAdPreview {
  kind: "google_search"
  id: string
  label: string | null
  headlines: string[]
  descriptions: string[]
  finalUrl: string | null
  displayUrl: string
  siteLinks: Array<{ title: string; url: string }>
}

type AdPreview = MetaAdPreview | GoogleSearchAdPreview

function MetaAdPreviewModal({ ad, onClose }: { ad: MetaAdPreview; onClose: () => void }) {
  return (
    <div className="media-zoom">
      <button
        aria-label="Close ad preview"
        className="media-zoom__backdrop"
        type="button"
        onClick={onClose}
      />
      <div
        aria-label="Meta ad preview"
        aria-modal="true"
        className="media-zoom__dialog meta-ad-preview-modal"
        role="dialog"
      >
        <div className="media-zoom__header">
          <strong>Meta ad preview</strong>
          <Button size="xs" type="button" variant="subtle" onClick={onClose}>
            Close
          </Button>
        </div>
        <article className="meta-ad-preview-card">
          <header className="meta-ad-preview-card__header">
            <div className="meta-ad-preview-card__avatar">
              {ad.pageName.slice(0, 1).toUpperCase()}
            </div>
            <div>
              <strong>{ad.pageName}</strong>
              <span>Sponsored</span>
            </div>
          </header>
          <p className="meta-ad-preview-card__text">{ad.primaryText}</p>
          {ad.imageUrl ? (
            <img alt={ad.headline} className="meta-ad-preview-card__image" src={ad.imageUrl} />
          ) : (
            <div className="meta-ad-preview-card__image meta-ad-preview-card__image--empty" />
          )}
          <footer className="meta-ad-preview-card__footer">
            <div>
              {ad.finalUrl ? <span>{hostLabel(ad.finalUrl)}</span> : null}
              <strong>{ad.headline}</strong>
              {ad.description ? <p>{ad.description}</p> : null}
            </div>
            {ad.finalUrl ? (
              <a href={ad.finalUrl} rel="noreferrer" target="_blank">
                {ad.cta}
              </a>
            ) : (
              <button type="button">{ad.cta}</button>
            )}
          </footer>
        </article>
      </div>
    </div>
  )
}

function buildMetaAdPreviews(detail: CampaignChannelDetailState): MetaAdPreview[] {
  if (detail.channel.provider !== "meta") return []
  const assetsById = new Map(detail.assets.map((asset) => [asset.assetId, asset]))
  const creativeById = new Map(
    detail.entities
      .filter((entity) => entity.entityType === "ad_creative")
      .map((entity) => [entity.externalEntityId, entity])
  )
  const pageName = detail.channel.name ?? "Adgiro"
  return detail.entities
    .filter((entity) => entity.entityType === "ad")
    .map((entity): MetaAdPreview | null => {
      const mediaAsset = assetsById.get(metadataString(entity.metadata, "mediaAssetId") ?? "")
      const copyAssets = metadataStringArray(entity.metadata, "copyAssetIds")
        .map((assetId) => assetsById.get(assetId))
        .filter((asset): asset is SiteAssetRecord => Boolean(asset))
      const headline =
        firstAssetText(copyAssets, "headline") ?? entity.name ?? detail.channel.name ?? "Learn more"
      const description =
        firstAssetText(copyAssets, "description") ?? firstAssetText(copyAssets, "proof_point")
      const primaryText = firstAssetText(copyAssets, "offer") ?? description ?? headline
      const creativeId = metadataString(entity.metadata, "creativeId")
      const creative = creativeId ? creativeById.get(creativeId) : null
      return {
        kind: "meta",
        id: entity.campaignEntityId,
        pageName,
        primaryText,
        headline,
        description,
        imageUrl: mediaAsset?.valueUrl ?? null,
        finalUrl:
          metadataString(entity.metadata, "finalUrl") ??
          metadataString(creative?.metadata ?? null, "finalUrl"),
        cta: "Learn More",
      }
    })
    .filter((preview): preview is MetaAdPreview => Boolean(preview))
}

function GoogleSearchAdPreviewModal({
  ad,
  onClose,
}: {
  ad: GoogleSearchAdPreview
  onClose: () => void
}) {
  const title = ad.headlines.slice(0, 3).join(" | ")
  return (
    <div className="media-zoom">
      <button
        aria-label="Close ad preview"
        className="media-zoom__backdrop"
        type="button"
        onClick={onClose}
      />
      <div
        aria-label="Google Search ad preview"
        aria-modal="true"
        className="media-zoom__dialog google-search-preview-modal"
        role="dialog"
      >
        <div className="media-zoom__header">
          <strong>Google Search preview</strong>
          <Button size="xs" type="button" variant="subtle" onClick={onClose}>
            Close
          </Button>
        </div>
        <article className="google-search-preview">
          <div className="google-search-preview__label">Sponsored</div>
          {ad.label ? <div className="google-search-preview__label">{ad.label}</div> : null}
          <div className="google-search-preview__url">{ad.displayUrl}</div>
          <h3>{title || "Search ad"}</h3>
          {ad.descriptions.slice(0, 2).map((description) => (
            <p key={description}>{description}</p>
          ))}
          {ad.siteLinks.length > 0 ? (
            <div className="google-search-preview__sitelinks">
              {ad.siteLinks.slice(0, 4).map((siteLink) => (
                <a
                  key={`${siteLink.title}:${siteLink.url}`}
                  href={siteLink.url}
                  rel="noreferrer"
                  target="_blank"
                >
                  {siteLink.title}
                </a>
              ))}
            </div>
          ) : null}
          {ad.finalUrl ? (
            <a
              className="google-search-preview__landing"
              href={ad.finalUrl}
              rel="noreferrer"
              target="_blank"
            >
              Open landing page
            </a>
          ) : null}
        </article>
      </div>
    </div>
  )
}

function buildGoogleSearchAdPreviews(detail: CampaignChannelDetailState): GoogleSearchAdPreview[] {
  if (detail.channel.channel !== "google_search") return []
  const fallbackHeadlines = assetTexts(detail.assets, ["headline", "offer", "proof_point"], 3)
  const fallbackDescriptions = assetTexts(detail.assets, ["description", "offer", "proof_point"], 2)
  const fallbackFinalUrl =
    detail.assets.find((asset) => asset.assetType === "landing_page")?.valueUrl ?? null
  const siteLinks = detail.assets
    .filter((asset) => asset.assetType === "site_link" && asset.valueUrl)
    .map((asset) => ({
      title: asset.valueText || asset.name || shortUrlLabel(asset.valueUrl ?? ""),
      url: asset.valueUrl ?? "",
    }))
  return detail.entities
    .filter((entity) => entity.entityType === "ad")
    .map((entity) => {
      const adHeadlines = metadataStringArray(entity.metadata, "headlines")
      const adDescriptions = metadataStringArray(entity.metadata, "descriptions")
      const finalUrl = metadataString(entity.metadata, "finalUrl") ?? fallbackFinalUrl
      const adGroupName = metadataString(entity.metadata, "adGroupName")
      const headlineFallbacks =
        fallbackHeadlines.length > 0
          ? fallbackHeadlines
          : [entity.name, adGroupName, detail.channel.name]
      const descriptionFallbacks = [
        ...adDescriptions,
        ...fallbackDescriptions,
        adGroupName ? `For ${adGroupName}` : null,
      ]
      return {
        kind: "google_search" as const,
        id: entity.campaignEntityId,
        label: uniqueNonEmptyStrings([entity.name, adGroupName]).join(" / ") || null,
        headlines: uniqueNonEmptyStrings([...adHeadlines, ...headlineFallbacks]).slice(0, 3),
        descriptions: uniqueNonEmptyStrings(descriptionFallbacks).slice(0, 2),
        finalUrl,
        displayUrl: finalUrl ? shortUrlLabel(finalUrl) : "Landing page",
        siteLinks,
      }
    })
}

function uniqueNonEmptyStrings(values: Array<string | null | undefined>): string[] {
  const result: string[] = []
  for (const value of values) {
    const cleaned = value?.trim()
    if (!cleaned || result.includes(cleaned)) continue
    result.push(cleaned)
  }
  return result
}

function assetTexts(assets: SiteAssetRecord[], assetTypes: string[], limit: number): string[] {
  const values: string[] = []
  for (const assetType of assetTypes) {
    for (const asset of assets) {
      if (asset.assetType !== assetType || !asset.valueText) continue
      if (values.includes(asset.valueText)) continue
      values.push(asset.valueText)
      if (values.length >= limit) return values
    }
  }
  return values
}

function firstAssetText(assets: SiteAssetRecord[], assetType: string): string | null {
  return assets.find((asset) => asset.assetType === assetType)?.valueText ?? null
}

function hostLabel(value: string): string {
  try {
    return new URL(value).hostname.replace(/^www\./, "").toUpperCase()
  } catch {
    return value.toUpperCase()
  }
}

function shortUrlLabel(value: string): string {
  try {
    const url = new URL(value)
    return `${url.hostname.replace(/^www\./, "")}${url.pathname === "/" ? "" : url.pathname}`
  } catch {
    return value
  }
}

function CampaignEntityTypeBadge({ entity }: { entity: CampaignEntityRecord }) {
  return (
    <StatusBadge color={campaignEntityTypeColor(entity.entityType)}>
      {campaignEntityTypeLabel(entity.entityType)}
    </StatusBadge>
  )
}

function CampaignEntityName({
  entity,
  depth = 0,
}: {
  entity: CampaignEntityRecord
  depth?: number
}) {
  const primary = campaignEntityDisplayName(entity)
  const secondary = campaignEntitySecondaryLabel(entity, primary)
  return (
    <div className="campaign-entity-name">
      {depth > 0 ? <span aria-hidden="true" className="campaign-entity-tree-marker" /> : null}
      <span className="campaign-entity-name-copy">
        <span>{primary}</span>
        {secondary ? <small>{secondary}</small> : null}
      </span>
    </div>
  )
}

function CampaignEntityStatusBadge({ entity }: { entity: CampaignEntityRecord }) {
  if (entity.entityType === "ad_creative") {
    return <StatusBadge color="gray">Created</StatusBadge>
  }
  return (
    <StatusBadge color={campaignEntityStatusColor(entity.status)}>
      {statusLabel(entity.status)}
    </StatusBadge>
  )
}

function CampaignEntityDetails({
  entity,
  preview,
  onPreview,
}: {
  entity: CampaignEntityRecord
  preview?: AdPreview
  onPreview?: (preview: AdPreview) => void
}) {
  const details = campaignEntityDetails(entity)
  if (preview) {
    return (
      <div className="campaign-entity-details">
        {details.map((detail) => (
          <span key={detail}>{detail}</span>
        ))}
        <Button size="xs" type="button" variant="subtle" onClick={() => onPreview?.(preview)}>
          Preview
        </Button>
      </div>
    )
  }
  if (details.length === 0) {
    return (
      <span className="muted-text">
        {entity.entityType === "ad_creative" ? "Creative receipt" : "Provider receipt"}
      </span>
    )
  }
  return (
    <div className="campaign-entity-details">
      {details.map((detail) => (
        <span key={detail}>{detail}</span>
      ))}
    </div>
  )
}

function CampaignEntitySourceLink({
  channel,
  entity,
}: {
  channel: CampaignChannelRecord
  entity: CampaignEntityRecord
}) {
  const source = campaignEntitySource(entity)
  if (!source) return <span className="muted-text">Provider</span>
  return (
    <Link className="inline-link-button" href={campaignEntitySourceUrl(channel, source)}>
      {source.label}
    </Link>
  )
}

function GoogleSearchTermsPanel({
  busy,
  detail,
  onAddNegative,
}: {
  busy: boolean
  detail: CampaignChannelDetailState
  onAddNegative: (term: GoogleSearchTermDiagnostic) => void
}) {
  if (detail.channel.channel !== "google_search") return null
  const googleSearch = detail.providerDiagnostics?.googleSearch
  const terms = googleSearch?.searchTerms ?? []
  const spendSummary = googleSearch?.spendSummary ?? null
  const negativeRules = googleSearchNegativeRules(detail)
  return (
    <div className="campaign-search-terms">
      <SectionHeader
        title="Search terms"
        description={
          terms.length > 0
            ? "Search term rows from the last 30 days, sorted by cost."
            : detail.latestSnapshot
              ? "No search terms were returned in the latest provider stats pull."
              : "Pull stats to inspect matched queries."
        }
      />
      {spendSummary ? <GoogleSearchSpendSummaryPanel spendSummary={spendSummary} /> : null}
      {terms.length > 0 ? (
        <DataTableShell minWidth={760}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Term</Table.Th>
              <Table.Th>Source</Table.Th>
              <Table.Th>Impr</Table.Th>
              <Table.Th>Clicks</Table.Th>
              <Table.Th>Cost</Table.Th>
              <Table.Th>Conv</Table.Th>
              <Table.Th>Negative</Table.Th>
              <Table.Th>Action</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {terms.map((term) => {
              const coveredBy = negativeRules.find((rule) =>
                negativeKeywordCoversSearchTerm(rule, term.searchTerm)
              )
              return (
                <Table.Tr key={`${term.searchTerm}:${term.matchedKeyword ?? ""}`}>
                  <Table.Td>
                    <strong>{term.searchTerm}</strong>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      {term.matchedKeyword ? (
                        <span>
                          {term.matchedKeyword}
                          {term.matchType ? ` · ${term.matchType}` : ""}
                        </span>
                      ) : (
                        <span className="muted-text">Unknown keyword</span>
                      )}
                      <Text c="dimmed" size="xs">
                        {term.adGroupResourceName
                          ? `Ad group ${googleResourceTail(term.adGroupResourceName)}`
                          : "Campaign term"}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>{formatInteger(term.impressions)}</Table.Td>
                  <Table.Td>{formatInteger(term.clicks)}</Table.Td>
                  <Table.Td>{formatMicrosCurrency(term.costMicros)}</Table.Td>
                  <Table.Td>{formatDecimal(term.conversions)}</Table.Td>
                  <Table.Td>
                    {coveredBy ? (
                      <Group gap={6} wrap="nowrap">
                        <StatusBadge color="green">Covered</StatusBadge>
                        <Text c="dimmed" size="xs">
                          Blocked by {coveredBy.text} · {coveredBy.matchType}
                        </Text>
                      </Group>
                    ) : (
                      <span className="muted-text">Open</span>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Button
                      disabled={busy || Boolean(coveredBy)}
                      size="xs"
                      type="button"
                      variant="subtle"
                      onClick={() => onAddNegative(term)}
                    >
                      {coveredBy ? "Covered" : "Add negative"}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </DataTableShell>
      ) : null}
    </div>
  )
}

function GoogleSearchSpendSummaryPanel({
  spendSummary,
}: {
  spendSummary: NonNullable<
    NonNullable<CampaignChannelDetailState["providerDiagnostics"]>["googleSearch"]
  >["spendSummary"]
}) {
  if (!spendSummary) return null
  const hiddenCount = spendSummary.returnedSearchTermCount - spendSummary.visibleSearchTermCount
  return (
    <div className="campaign-search-spend-summary">
      <div>
        <span>Campaign 30d</span>
        <strong>{formatMicrosCurrency(spendSummary.campaignCostMicros30Days)}</strong>
      </div>
      <div>
        <span>Returned terms</span>
        <strong>{formatMicrosCurrency(spendSummary.returnedSearchTermCostMicros30Days)}</strong>
      </div>
      <div>
        <span>Shown terms</span>
        <strong>{formatMicrosCurrency(spendSummary.visibleSearchTermCostMicros30Days)}</strong>
      </div>
      <div>
        <span>Other campaign spend</span>
        <strong>{formatMicrosCurrency(spendSummary.unattributedCampaignCostMicros30Days)}</strong>
      </div>
      <small>
        {`${String(spendSummary.visibleSearchTermCount)} of ${String(
          spendSummary.returnedSearchTermCount
        )} returned rows shown. Hidden returned-row spend: ${formatMicrosCurrency(
          spendSummary.hiddenReturnedSearchTermCostMicros30Days
        )}${hiddenCount > 0 ? ` across ${String(hiddenCount)} rows` : ""}.`}
      </small>
    </div>
  )
}

function MetaAdResultsPanel({ detail }: { detail: CampaignChannelDetailState }) {
  if (detail.channel.channel !== "meta_ads") return null
  const adResults = detail.providerDiagnostics?.metaAds?.adResults ?? []
  return (
    <div className="campaign-search-terms">
      <SectionHeader
        title="Meta ad results"
        description={
          adResults.length > 0
            ? "Ad-level Meta results from the last 30 days for this campaign channel."
            : detail.latestSnapshot
              ? "No ad-level Meta results were returned in the latest provider stats pull."
              : "Pull stats to inspect Meta ad results."
        }
      />
      {adResults.length > 0 ? (
        <DataTableShell minWidth={760}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ad</Table.Th>
              <Table.Th>Ad set</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Impr</Table.Th>
              <Table.Th>Clicks</Table.Th>
              <Table.Th>Cost</Table.Th>
              <Table.Th>Conv</Table.Th>
              <Table.Th>CPA</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {adResults.map((result) => (
              <Table.Tr key={result.adId}>
                <Table.Td>
                  <MetaAdResultName result={result} />
                </Table.Td>
                <Table.Td>
                  {result.adSetName ?? result.adSetId ?? (
                    <span className="muted-text">Unknown</span>
                  )}
                </Table.Td>
                <Table.Td>
                  <StatusBadge
                    color={campaignEntityStatusColor(result.effectiveStatus ?? result.status ?? "")}
                  >
                    {statusLabel(result.effectiveStatus ?? result.status ?? "unknown")}
                  </StatusBadge>
                </Table.Td>
                <Table.Td>{formatInteger(result.impressions)}</Table.Td>
                <Table.Td>{formatInteger(result.clicks)}</Table.Td>
                <Table.Td>{formatMicrosCurrency(result.costMicros)}</Table.Td>
                <Table.Td>{formatDecimal(result.conversions)}</Table.Td>
                <Table.Td>
                  {result.conversions > 0 ? (
                    formatMicrosCurrency(result.costMicros / result.conversions)
                  ) : (
                    <span className="muted-text">-</span>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </DataTableShell>
      ) : null}
    </div>
  )
}

function MetaAdResultName({ result }: { result: MetaAdResultDiagnostic }) {
  return (
    <div className="campaign-entity-name">
      <span>{result.adName ?? "Meta ad"}</span>
      <small>{result.adId}</small>
    </div>
  )
}

type GoogleSearchNegativeRule = {
  text: string
  matchType: "BROAD" | "EXACT" | "PHRASE"
  source: "asset" | "provider"
  resourceName: string | null
}

function googleSearchNegativeRules(detail: CampaignChannelDetailState): GoogleSearchNegativeRule[] {
  const removedProviderNegatives = removedGoogleSearchNegativeRules(detail.entities)
  const assetRules = detail.assets
    .filter((asset) => asset.assetType === "negative_keyword")
    .flatMap((asset): GoogleSearchNegativeRule[] => {
      const text = cleanSearchTerm(asset.valueText ?? asset.name)
      if (!text) return []
      return [
        {
          text,
          matchType: negativeAssetMatchType(asset),
          source: "asset",
          resourceName: null,
        },
      ]
    })
  const providerRules =
    detail.providerDiagnostics?.googleSearch?.campaignNegatives?.flatMap(
      (negative: GoogleSearchNegativeDiagnostic): GoogleSearchNegativeRule[] => {
        const text = cleanSearchTerm(negative.text)
        if (!text) return []
        const rule = {
          text,
          matchType: negative.matchType ?? "BROAD",
          source: "provider" as const,
          resourceName: negative.resourceName,
        }
        if (isRemovedProviderNegative(rule, removedProviderNegatives)) return []
        return [rule]
      }
    ) ?? []
  return [...assetRules, ...providerRules]
}

function removedGoogleSearchNegativeRules(
  entities: CampaignEntityRecord[]
): GoogleSearchNegativeRule[] {
  return entities
    .filter((entity) => entity.entityType === "negative_keyword" && entity.status === "removed")
    .flatMap((entity): GoogleSearchNegativeRule[] => {
      const text = metadataString(entity.metadata, "keywordText")
      const matchType = metadataString(entity.metadata, "matchType")
      if (!text || (matchType !== "EXACT" && matchType !== "PHRASE" && matchType !== "BROAD")) {
        return []
      }
      return [
        {
          text: cleanSearchTerm(text),
          matchType,
          source: "provider",
          resourceName: entity.externalEntityId,
        },
      ]
    })
}

function isRemovedProviderNegative(
  rule: GoogleSearchNegativeRule,
  removedRules: GoogleSearchNegativeRule[]
): boolean {
  return removedRules.some((removed) => {
    if (removed.resourceName && rule.resourceName) return removed.resourceName === rule.resourceName
    return (
      removed.matchType === rule.matchType &&
      cleanSearchTerm(removed.text).toLocaleLowerCase() ===
        cleanSearchTerm(rule.text).toLocaleLowerCase()
    )
  })
}

function googleResourceTail(resourceName: string): string {
  return resourceName.split("/").at(-1) ?? resourceName
}

function negativeAssetMatchType(asset: SiteAssetRecord): "BROAD" | "EXACT" | "PHRASE" {
  const value = asset.valueJson?.matchType
  return value === "EXACT" || value === "PHRASE" || value === "BROAD" ? value : "BROAD"
}

function negativeKeywordCoversSearchTerm(
  rule: GoogleSearchNegativeRule,
  searchTerm: string
): boolean {
  const negativeTokens = searchTokens(rule.text)
  const searchTokensValue = searchTokens(searchTerm)
  if (negativeTokens.length === 0 || searchTokensValue.length === 0) return false
  if (rule.matchType === "EXACT") return negativeTokens.join(" ") === searchTokensValue.join(" ")
  if (rule.matchType === "PHRASE") return containsTokenPhrase(searchTokensValue, negativeTokens)
  return negativeTokens.every((token) => searchTokensValue.includes(token))
}

function containsTokenPhrase(searchTokensValue: string[], negativeTokens: string[]): boolean {
  if (negativeTokens.length > searchTokensValue.length) return false
  return searchTokensValue.some((_, index) =>
    negativeTokens.every((token, offset) => searchTokensValue[index + offset] === token)
  )
}

function searchTokens(value: string): string[] {
  return cleanSearchTerm(value).toLocaleLowerCase().split(/\s+/).filter(Boolean)
}

function cleanSearchTerm(value: string): string {
  return value.trim().replace(/\s+/g, " ")
}

function CampaignChannelSnapshotSummary({ detail }: { detail: CampaignChannelDetailState }) {
  const snapshot = detail.latestSnapshot
  if (!snapshot) {
    return (
      <div className="campaign-channel-snapshot">
        <SectionHeader
          title="Provider stats"
          description={
            detail.channel.externalCampaignId
              ? "No provider stats have been pulled yet."
              : "Launch this channel before pulling provider stats."
          }
        />
      </div>
    )
  }

  const summary = snapshot.summary ?? {}
  const metricsToday = summaryRecord(summary, "metrics.today")
  const metricsYesterday = summaryRecord(summary, "metrics.yesterday")
  const metrics7 = summaryRecord(summary, "metrics.last7Days")
  const metrics30 = summaryRecord(summary, "metrics.last30Days")
  const entityCounts = summaryRecord(summary, "entityCounts")
  const networkSettings = summaryRecord(summary, "networkSettings")
  const issues = summaryArray(summary, "issues")
  const groupedIssues = groupedProviderIssues(issues)
  const hasMetaCounts = detail.channel.provider === "meta"
  const searchExpansionEnabled =
    detail.channel.channel === "google_search" &&
    (summaryBoolean(networkSettings.targetSearchNetwork) === true ||
      summaryBoolean(networkSettings.targetContentNetwork) === true ||
      summaryBoolean(networkSettings.targetPartnerSearchNetwork) === true)

  return (
    <div className="campaign-channel-snapshot">
      <SectionHeader
        title="Provider stats"
        description={`${statusLabel(snapshot.status)} · day, 7d, and 30d ranges · pulled ${formatDateTime(
          snapshot.capturedAt
        )}`}
      />
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="xs">
        <MetricCard
          label="Cost today"
          value={formatMicrosCurrency(summaryNumber(metricsToday.costMicros) ?? 0)}
        />
        <MetricCard
          label="Conversions today"
          value={formatDecimal(summaryNumber(metricsToday.conversions) ?? 0)}
        />
        <MetricCard
          label="Cost yesterday"
          value={formatMicrosCurrency(summaryNumber(metricsYesterday.costMicros) ?? 0)}
        />
        <MetricCard
          label="Conversions yesterday"
          value={formatDecimal(summaryNumber(metricsYesterday.conversions) ?? 0)}
        />
        <MetricCard label="Clicks 7d" value={formatInteger(summaryNumber(metrics7.clicks) ?? 0)} />
        <MetricCard
          label="Cost 7d"
          value={formatMicrosCurrency(summaryNumber(metrics7.costMicros) ?? 0)}
        />
        <MetricCard
          label="Conversions 7d"
          value={formatDecimal(summaryNumber(metrics7.conversions) ?? 0)}
        />
        <MetricCard
          label="Clicks 30d"
          value={formatInteger(summaryNumber(metrics30.clicks) ?? 0)}
        />
        <MetricCard
          label="Cost 30d"
          value={formatMicrosCurrency(summaryNumber(metrics30.costMicros) ?? 0)}
        />
        <MetricCard
          label="Conversions 30d"
          value={formatDecimal(summaryNumber(metrics30.conversions) ?? 0)}
        />
      </SimpleGrid>
      <div className="site-readiness-grid site-readiness-grid--inline campaign-channel-counts">
        <div className="site-readiness-item">
          <span>{hasMetaCounts ? "Ad sets" : "Ad groups"}</span>
          <strong>
            {formatInteger(
              summaryNumber(hasMetaCounts ? entityCounts.adSets : entityCounts.adGroups) ?? 0
            )}
          </strong>
        </div>
        <div className="site-readiness-item">
          <span>Ads</span>
          <strong>{formatInteger(summaryNumber(entityCounts.ads) ?? 0)}</strong>
        </div>
        <div className="site-readiness-item">
          <span>{hasMetaCounts ? "Pending review" : "Keywords"}</span>
          <strong>
            {formatInteger(
              summaryNumber(
                hasMetaCounts ? entityCounts.pendingReviewAds : entityCounts.keywords
              ) ?? 0
            )}
          </strong>
        </div>
        <div className="site-readiness-item">
          <span>{hasMetaCounts ? "Hard issues" : "Negatives"}</span>
          <strong>
            {formatInteger(
              summaryNumber(
                hasMetaCounts ? entityCounts.hardIssues : entityCounts.negativeKeywords
              ) ?? 0
            )}
          </strong>
        </div>
        <div className="site-readiness-item">
          <span>{hasMetaCounts ? "Soft issues" : "Search terms"}</span>
          <strong>
            {formatInteger(
              summaryNumber(hasMetaCounts ? entityCounts.softIssues : entityCounts.searchTerms) ?? 0
            )}
          </strong>
        </div>
      </div>
      {issues.length > 0 ? (
        <div className="campaign-channel-issues">
          {groupedIssues.slice(0, 4).map((issue) => {
            return (
              <div className="campaign-channel-issue" key={issue.key}>
                <strong>{issue.summary}</strong>
                <span>{issue.message}</span>
                {issue.count > 1 ? <small>{affectedProviderIssueLabel(issue)}</small> : null}
                {detail.channel.providerSupportUrl ? (
                  <a
                    className="inline-link-button"
                    href={detail.channel.providerSupportUrl}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Review in {providerLabel(detail.channel.provider)}
                  </a>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : null}
      {searchExpansionEnabled ? (
        <div className="campaign-network-warning">
          This campaign has Google Search Network, Display Network, or search partner expansion
          enabled in Google Ads. Search-term rows may not add up to campaign spend until sync
          disables expansion.
        </div>
      ) : null}
    </div>
  )
}

function CampaignOptimizationSummary({ detail }: { detail: CampaignChannelDetailState }) {
  const run = detail.latestOptimizationRun
  if (!run) return null
  const actions = detail.latestOptimizationActions ?? []
  const proposed = actions.filter((action) => action.status === "proposed")
  const dryRun =
    summaryBoolean(run.inputs?.dryRun) === true || summaryBoolean(run.output?.dryRun) === true
  const visibleActions = proposed.length > 0 ? proposed : actions.slice(0, 5)
  return (
    <div className="campaign-optimization-summary">
      <SectionHeader
        title={dryRun ? "Optimization recommendations" : "Latest optimization"}
        description={`${statusLabel(run.status)} · ${run.summary ?? "No summary"} · ${formatDateTime(
          run.createdAt
        )}`}
      />
      {visibleActions.length > 0 ? (
        <div className="campaign-optimization-actions">
          {visibleActions.map((action) => (
            <div className="campaign-optimization-action" key={action.agentActionId}>
              <span>{statusLabel(action.status)}</span>
              <strong>{action.title}</strong>
              {action.rationale ? <small>{action.rationale}</small> : null}
            </div>
          ))}
        </div>
      ) : (
        <Text c="dimmed" size="sm">
          No optimization actions recorded.
        </Text>
      )}
    </div>
  )
}

function summaryRecord(value: Record<string, unknown>, path: string): Record<string, unknown> {
  const resolved = path.split(".").reduce<unknown>((current, part) => {
    if (!current || typeof current !== "object" || Array.isArray(current)) return null
    return (current as Record<string, unknown>)[part] ?? null
  }, value)
  return resolved && typeof resolved === "object" && !Array.isArray(resolved)
    ? (resolved as Record<string, unknown>)
    : {}
}

function summaryArray(value: Record<string, unknown>, path: string): unknown[] {
  const resolved = path.split(".").reduce<unknown>((current, part) => {
    if (!current || typeof current !== "object" || Array.isArray(current)) return null
    return (current as Record<string, unknown>)[part] ?? null
  }, value)
  return Array.isArray(resolved) ? resolved : []
}

interface GroupedProviderIssue {
  key: string
  summary: string
  message: string
  count: number
  entityCounts: Map<string, number>
}

function groupedProviderIssues(issues: unknown[]): GroupedProviderIssue[] {
  const groups = new Map<string, GroupedProviderIssue>()
  for (const issue of issues) {
    const row = issue && typeof issue === "object" ? (issue as Record<string, unknown>) : {}
    const summary = summaryString(row.summary) ?? "Provider issue"
    const message = summaryString(row.message) ?? "Review this in the provider."
    const key = [summaryString(row.errorType), summaryString(row.errorCode), summary, message].join(
      ":"
    )
    const group = groups.get(key) ?? {
      key,
      summary,
      message,
      count: 0,
      entityCounts: new Map<string, number>(),
    }
    group.count += 1
    const entityType = summaryString(row.entityType) ?? "item"
    group.entityCounts.set(entityType, (group.entityCounts.get(entityType) ?? 0) + 1)
    groups.set(key, group)
  }
  return Array.from(groups.values())
}

function affectedProviderIssueLabel(issue: GroupedProviderIssue): string {
  const entityLabels = Array.from(issue.entityCounts.entries()).map(
    ([entityType, count]) =>
      `${formatInteger(count)} ${providerIssueEntityLabel(entityType, count)}`
  )
  return `Affects ${formatInteger(issue.count)} ${issue.count === 1 ? "item" : "items"}${
    entityLabels.length > 0 ? `: ${entityLabels.join(", ")}` : ""
  }`
}

function providerIssueEntityLabel(entityType: string, count: number): string {
  const normalized = entityType.replace(/_/g, " ")
  const singular = normalized === "ad set" ? "ad set" : normalized
  return count === 1 ? singular : `${singular}s`
}

function formatInteger(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value)
}

function campaignEntityRows(
  entities: CampaignEntityRecord[]
): Array<{ entity: CampaignEntityRecord; depth: number }> {
  const childrenByParent = new Map<string, CampaignEntityRecord[]>()
  const entityIds = new Set(entities.map((entity) => entity.campaignEntityId))
  const entityIdsByExternalId = new Map(
    entities.map((entity) => [entity.externalEntityId, entity.campaignEntityId])
  )
  for (const entity of entities) {
    const parentId = campaignEntityParentId(entity, entityIds, entityIdsByExternalId)
    if (!parentId) continue
    const children = childrenByParent.get(parentId) ?? []
    children.push(entity)
    childrenByParent.set(parentId, children)
  }
  for (const children of childrenByParent.values()) children.sort(compareCampaignEntities)
  const rows: Array<{ entity: CampaignEntityRecord; depth: number }> = []
  const seen = new Set<string>()
  const addEntity = (entity: CampaignEntityRecord, depth: number) => {
    if (seen.has(entity.campaignEntityId)) return
    seen.add(entity.campaignEntityId)
    rows.push({ entity, depth })
    for (const child of childrenByParent.get(entity.campaignEntityId) ?? [])
      addEntity(child, depth + 1)
  }
  for (const entity of [...entities].sort(compareCampaignEntities)) {
    const parentId = campaignEntityParentId(entity, entityIds, entityIdsByExternalId)
    if (parentId) continue
    addEntity(entity, 0)
  }
  for (const entity of [...entities].sort(compareCampaignEntities)) addEntity(entity, 0)
  return rows
}

function campaignEntityParentId(
  entity: CampaignEntityRecord,
  entityIds: Set<string>,
  entityIdsByExternalId: Map<string, string>
): string | null {
  if (entity.parentCampaignEntityId && entityIds.has(entity.parentCampaignEntityId)) {
    return entity.parentCampaignEntityId
  }
  if (!entity.externalParentEntityId) return null
  return entityIdsByExternalId.get(entity.externalParentEntityId) ?? null
}

function compareCampaignEntities(left: CampaignEntityRecord, right: CampaignEntityRecord): number {
  const weightDelta =
    campaignEntitySortWeight(left.entityType) - campaignEntitySortWeight(right.entityType)
  if (weightDelta !== 0) return weightDelta
  return campaignEntityDisplayName(left).localeCompare(campaignEntityDisplayName(right))
}

function campaignEntitySortWeight(entityType: string): number {
  const weights: Record<string, number> = {
    campaign: 0,
    campaign_budget: 1,
    custom_conversion_goal: 2,
    ad_group: 3,
    asset_group: 3,
    ad_set: 3,
    ad: 4,
    ad_creative: 4,
    keyword: 5,
    negative_keyword: 6,
    asset: 7,
  }
  return weights[entityType] ?? 9
}

function campaignEntityDisplayName(entity: CampaignEntityRecord): string {
  const sourceName = metadataString(entity.metadata, "sourceName")
  if (entity.entityType === "asset" && sourceName) return sourceName
  return (
    metadataString(entity.metadata, "text") ??
    metadataString(entity.metadata, "keywordText") ??
    entity.name ??
    `${campaignEntityTypeLabel(entity.entityType)} receipt`
  )
}

function campaignEntitySecondaryLabel(
  entity: CampaignEntityRecord,
  primary: string
): string | null {
  const sourceAssetType = metadataString(entity.metadata, "sourceAssetType")
  const sourceName = metadataString(entity.metadata, "sourceName")
  const text =
    metadataString(entity.metadata, "text") ?? metadataString(entity.metadata, "keywordText")
  const labels = [
    entity.name && entity.name !== primary ? `Provider: ${entity.name}` : null,
    sourceName && sourceName !== primary ? `Source: ${sourceName}` : null,
    text && text !== primary ? `Text: ${text}` : null,
    sourceAssetType ? `Asset type: ${sourceAssetType}` : null,
    !entity.metadata ? "Provider receipt without local metadata" : null,
  ].filter((label): label is string => Boolean(label))
  return labels[0] ?? null
}

function campaignEntityDetails(entity: CampaignEntityRecord): string[] {
  const fieldTypes = metadataStringArray(entity.metadata, "fieldTypes")
  const providerAssetType = metadataString(entity.metadata, "providerAssetType")
  const matchType = metadataString(entity.metadata, "matchType")
  const syncMode = metadataString(entity.metadata, "syncMode")
  const finalUrl = metadataString(entity.metadata, "finalUrl")
  const dimensions = [
    metadataNumber(entity.metadata, "width"),
    metadataNumber(entity.metadata, "height"),
  ]
  const dimensionLabel = dimensions[0] && dimensions[1] ? `${dimensions[0]}x${dimensions[1]}` : null
  const landingLabel = finalUrl ? `Landing: ${shortUrlLabel(finalUrl)}` : null
  return [
    providerAssetType,
    matchType,
    fieldTypes.join(", "),
    dimensionLabel,
    syncMode,
    landingLabel,
  ].filter((detail): detail is string => Boolean(detail))
}

function campaignEntityTypeLabel(entityType: string): string {
  const labels: Record<string, string> = {
    ad: "Ad",
    ad_group: "Ad group",
    asset: "Asset",
    asset_group: "Asset group",
    ad_creative: "Creative",
    campaign: "Campaign",
    campaign_budget: "Budget",
    keyword: "Keyword",
    negative_keyword: "Negative",
    search_theme: "Search theme",
  }
  return labels[entityType] ?? titleize(entityType)
}

function campaignEntityTypeColor(entityType: string): string {
  if (entityType === "campaign") return "blue"
  if (entityType === "asset_group" || entityType === "ad_group") return "violet"
  if (entityType === "asset" || entityType === "ad") return "teal"
  if (entityType === "keyword" || entityType === "search_theme") return "indigo"
  if (entityType === "negative_keyword") return "red"
  return "gray"
}

function campaignEntityStatusColor(status: string): string {
  if (status === "active" || status === "enabled") return "green"
  if (status === "paused") return "yellow"
  if (status === "failed" || status === "removed") return "red"
  return "gray"
}

function campaignStatusTone(status: string): string {
  if (status === "active") return "green"
  if (status === "archived") return "red"
  if (status === "paused") return "yellow"
  return "gray"
}

function campaignSyncStateColor(syncState: string): string {
  if (syncState === "provider_active" || syncState === "provider_paused") return "green"
  if (syncState === "queued" || syncState === "syncing") return "blue"
  if (syncState === "needs_sync") return "yellow"
  if (syncState === "failed") return "red"
  return "gray"
}

function titleize(value: string): string {
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(" ")
}

function campaignEntitySource(
  entity: CampaignEntityRecord
): { type: string; id: string; label: string; assetType: string | null } | null {
  const sourceType = metadataString(entity.metadata, "sourceType")
  const sourceId = metadataString(entity.metadata, "sourceId")
  if (!sourceType || !sourceId) return null
  const sourceName = metadataString(entity.metadata, "sourceName")
  const sourceAssetType = metadataString(entity.metadata, "sourceAssetType")
  return {
    type: sourceType,
    id: sourceId,
    label: sourceName || sourceAssetType || sourceType,
    assetType: sourceAssetType,
  }
}

function campaignEntitySourceUrl(
  channel: CampaignChannelRecord,
  source: { type: string; assetType: string | null }
): string {
  const base = `/app/sites/${encodeURIComponent(channel.siteId)}`
  if (source.type === "mapping") return `${base}?tab=tracking`
  const assetTab = source.assetType ? assetTabForAssetType(source.assetType) : "overview"
  const params = new URLSearchParams({
    segment: channel.segmentId,
  })
  if (assetTab !== "overview") params.set("assetTab", assetTab)
  return `${base}?${params.toString()}`
}

function assetTabForAssetType(assetType: string): AssetTab {
  if (assetType === "image" || assetType === "video") return "media"
  if (
    assetType === "location" ||
    assetType === "audience" ||
    assetType === "competitor" ||
    assetType === "search_intent" ||
    assetType === "keyword_theme" ||
    assetType === "negative_keyword"
  ) {
    return "targeting"
  }
  if (
    assetType === "headline" ||
    assetType === "description" ||
    assetType === "offer" ||
    assetType === "proof_point"
  ) {
    return "copy"
  }
  return "overview"
}

function metadataString(metadata: Record<string, unknown> | null, key: string): string | null {
  const value = metadata?.[key]
  return typeof value === "string" && value.trim() ? value : null
}

function metadataStringArray(metadata: Record<string, unknown> | null, key: string): string[] {
  const value = metadata?.[key]
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && Boolean(item.trim()))
    : []
}

function metadataNumber(metadata: Record<string, unknown> | null, key: string): number | null {
  const value = metadata?.[key]
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

function segmentReadinessMessage(
  readiness: CampaignGenerationState["segments"][number]["readiness"]
): string {
  const channelReadiness = readiness.channelReadiness ?? []
  if (channelReadiness.length > 0) {
    const readyCount = channelReadiness.filter((channel) => channel.ready).length
    return `${String(readyCount)}/${String(channelReadiness.length)} channels ready`
  }
  if (readiness.ready) return "Ready to sync"
  if (readiness.missing.length === 0) return "Needs review"
  return `Missing ${readiness.missing.join(", ")}`
}

function channelReadinessForRow(
  segmentState: CampaignGenerationState["segments"][number],
  channel: CampaignChannelKey
): CampaignChannelReadiness | null {
  return (
    (segmentState.readiness.channelReadiness ?? []).find(
      (candidate) => candidate.channel === channel
    ) ?? null
  )
}

type CampaignControlRow = {
  key: string
  segment: SiteSegmentRecord
  channelKey: CampaignChannelKey
  channel: CampaignChannelRecord | null
}

function campaignControlRows(
  segmentStates: CampaignGenerationState["segments"],
  showArchived: boolean
): CampaignControlRow[] {
  return segmentStates.flatMap((segmentState) =>
    segmentState.readiness.eligibleChannels.map((channelKey) => {
      const channel =
        segmentState.channels.find(
          (candidate) =>
            candidate.channel === channelKey && (showArchived || candidate.status !== "archived")
        ) ?? null
      return {
        key: channel?.campaignChannelId ?? `${segmentState.segment.segmentId}:${channelKey}`,
        segment: segmentState.segment,
        channelKey,
        channel,
      }
    })
  )
}

function campaignChannelDetailUrl(channel: CampaignChannelRecord): string {
  return `/app/sites/${encodeURIComponent(channel.siteId)}?tab=campaigns&campaignChannel=${encodeURIComponent(
    channel.campaignChannelId
  )}`
}

function CampaignTableControls({
  archivedCount,
  statsRange,
  showArchived,
  onStatsRangeChange,
  onShowArchivedChange,
}: {
  archivedCount: number
  statsRange: CampaignStatsRange
  showArchived: boolean
  onStatsRangeChange: (statsRange: CampaignStatsRange) => void
  onShowArchivedChange: (showArchived: boolean) => void
}) {
  return (
    <div className="table-filter-bar">
      <SegmentedControl
        aria-label="Campaign stats range"
        data={[
          { label: "Today", value: "today" },
          { label: "Yesterday", value: "yesterday" },
          { label: "7d", value: "last7Days" },
          { label: "30d", value: "last30Days" },
        ]}
        size="xs"
        value={statsRange}
        onChange={(value) => onStatsRangeChange(value as CampaignStatsRange)}
      />
      {archivedCount > 0 ? (
        <label>
          <input
            checked={showArchived}
            type="checkbox"
            onChange={(event) => onShowArchivedChange(event.target.checked)}
          />
          Show archived
        </label>
      ) : null}
    </div>
  )
}

function CampaignActionsMenu({
  busy,
  canSync,
  channel,
  onArchive,
  onSaveBudget,
  onSavePlannerInstructions,
  onSync,
  onUnarchive,
}: {
  busy: boolean
  canSync: boolean
  channel: CampaignGenerationState["channels"][number]
  onArchive: (campaignChannelId: string) => void
  onSaveBudget: () => void
  onSavePlannerInstructions: (campaignChannelId: string, plannerInstructions: string) => void
  onSync: (campaignChannelId: string) => void
  onUnarchive: (campaignChannelId: string) => void
}) {
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)
  const [editingPlannerInstructions, setEditingPlannerInstructions] = useState(false)
  const menuIsMobileSheet = menuPosition?.top === -1
  const unarchiveDisabled = busy || channel.status !== "archived"
  const syncDisabled =
    busy || !canSync || channel.status === "archived" || channel.syncState === "syncing"
  const archiveDisabled = busy || channel.status === "archived"

  if (syncDisabled && archiveDisabled && unarchiveDisabled) {
    return <span className="muted-text">No actions</span>
  }

  function toggleMenu(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    if (menuPosition) {
      setMenuPosition(null)
      return
    }
    const rect = event.currentTarget.getBoundingClientRect()
    const mobileSheet = window.matchMedia("(max-width: 720px)").matches
    if (mobileSheet) {
      setMenuPosition({ top: -1, left: -1 })
      return
    }
    const menuWidth = 188
    const menuHeight = 232
    const left = Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12))
    const top =
      rect.bottom + menuHeight + 12 > window.innerHeight
        ? Math.max(12, rect.top - menuHeight - 6)
        : rect.bottom + 6
    setMenuPosition({ top, left })
  }

  function runAction(action: (campaignChannelId: string) => void) {
    setMenuPosition(null)
    action(channel.campaignChannelId)
  }

  return (
    <>
      <button className="button button--ghost button--table" type="button" onClick={toggleMenu}>
        {busy ? "Working..." : "Actions"}
      </button>
      {menuPosition ? (
        <>
          <button
            aria-label="Close campaign actions"
            className="campaign-actions-menu__backdrop"
            type="button"
            onClick={() => setMenuPosition(null)}
          />
          <div
            className={
              menuIsMobileSheet
                ? "campaign-actions-menu__items campaign-actions-menu__items--sheet"
                : "campaign-actions-menu__items"
            }
            style={
              menuIsMobileSheet ? undefined : { top: menuPosition.top, left: menuPosition.left }
            }
          >
            <button
              disabled={unarchiveDisabled}
              type="button"
              onClick={() => runAction(onUnarchive)}
            >
              Unarchive
            </button>
            <button disabled={syncDisabled} type="button" onClick={() => runAction(onSync)}>
              {channel.externalCampaignId ? "Sync" : "Launch"}
            </button>
            <button
              disabled={busy}
              type="button"
              onClick={() => {
                setMenuPosition(null)
                onSaveBudget()
              }}
            >
              Save budget
            </button>
            <button
              disabled={busy}
              type="button"
              onClick={() => {
                setMenuPosition(null)
                setEditingPlannerInstructions(true)
              }}
            >
              Planner prompt
            </button>
            <button disabled={archiveDisabled} type="button" onClick={() => runAction(onArchive)}>
              Archive
            </button>
          </div>
        </>
      ) : null}
      {editingPlannerInstructions ? (
        <PlannerInstructionsModal
          busy={busy}
          channel={channel}
          onClose={() => setEditingPlannerInstructions(false)}
          onSave={(plannerInstructions) => {
            setEditingPlannerInstructions(false)
            onSavePlannerInstructions(channel.campaignChannelId, plannerInstructions)
          }}
        />
      ) : null}
    </>
  )
}

function PlannerInstructionsModal({
  busy,
  channel,
  onClose,
  onSave,
}: {
  busy: boolean
  channel: CampaignChannelRecord
  onClose: () => void
  onSave: (plannerInstructions: string) => void
}) {
  const [draft, setDraft] = useState(campaignPlannerInstructions(channel))
  return (
    <>
      <button
        aria-label="Close planner prompt editor"
        className="campaign-actions-menu__backdrop"
        type="button"
        onClick={onClose}
      />
      <div aria-modal="true" className="planner-prompt-modal" role="dialog">
        <Stack gap="sm">
          <div>
            <h3>Planner prompt</h3>
            <Text c="dimmed" size="sm">
              These instructions are included in the next planner run for this channel.
            </Text>
          </div>
          <Textarea
            autosize
            maxRows={10}
            minRows={6}
            maxLength={2000}
            placeholder={'Example: Do not mention "PPE", helmets, hard hats, or construction.'}
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <Group justify="flex-end" gap="xs">
            <Button size="xs" type="button" variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={busy} size="xs" type="button" onClick={() => onSave(draft)}>
              Save
            </Button>
          </Group>
        </Stack>
      </div>
    </>
  )
}

function campaignPlannerInstructions(channel: CampaignChannelRecord): string {
  const value = channel.config?.plannerInstructions
  return typeof value === "string" ? value : ""
}

function campaignChannelLabel(channel: CampaignChannelKey): string {
  switch (channel) {
    case "google_search":
      return "Google Search"
    case "google_pmax":
      return "Google PMax"
    case "meta_ads":
      return "Meta Ads"
  }
}
